import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Package2, MapPin, Calendar, Plus, ExternalLink, CheckCircle, Clock, Edit2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Badge from '../components/ui/Badge';

interface ShippingInfo {
  id: string;
  sales_order_id: string;
  shipping_address: string;
  shipping_method: string;
  tracking_number: string;
  estimated_delivery: string;
  actual_delivery: string;
  shipping_cost: number;
  status: string;
  carrier: string;
  notes: string;
  created_at: string;
  sales_orders?: {
    order_number: string;
    customer_name: string;
    status: string;
  };
}

interface SalesOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  total_amount: number;
  status: string;
  created_at: string;
}

const ShippingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingShipment, setEditingShipment] = useState<ShippingInfo | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [shipments, setShipments] = useState<ShippingInfo[]>([]);
  const [pendingOrders, setPendingOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    sales_order_id: '',
    shipping_address: '',
    shipping_method: '',
    tracking_number: '',
    estimated_delivery: '',
    shipping_cost: '',
    carrier: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    tracking_number: '',
    carrier: '',
    status: '',
    actual_delivery: '',
    notes: ''
  });

  useEffect(() => {
    loadShipments();
    loadPendingOrders();
  }, []);

  const loadShipments = async () => {
    try {
      const { data, error } = await supabase
        .from('shipping_info')
        .select(`
          *,
          sales_orders (
            order_number,
            customer_name,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setShipments(data || []);
    } catch (error) {
      console.error('Error loading shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('sales_orders')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingOrders(data || []);
    } catch (error) {
      console.error('Error loading pending orders:', error);
    }
  };

  const handleOrderSelect = async (orderId: string) => {
    const selectedOrder = pendingOrders.find(order => order.id === orderId);
    if (!selectedOrder) return;

    // Auto-populate shipping details from the order
    setFormData(prev => ({
      ...prev,
      sales_order_id: orderId,
      shipping_address: `${selectedOrder.customer_name}\n${selectedOrder.customer_phone || ''}\n${selectedOrder.customer_email || ''}`
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { error: shippingError } = await supabase
        .from('shipping_info')
        .insert([{
          ...formData,
          status: 'pending'
        }]);

      if (shippingError) throw shippingError;

      // Update sales order status
      const { error: orderError } = await supabase
        .from('sales_orders')
        .update({ status: 'processing' })
        .eq('id', formData.sales_order_id);

      if (orderError) throw orderError;

      setShowForm(false);
      loadShipments();
      loadPendingOrders();
    } catch (error) {
      console.error('Error creating shipment:', error);
    }
  };

  const handleEditClick = (shipment: ShippingInfo) => {
    setEditingShipment(shipment);
    setEditFormData({
      tracking_number: shipment.tracking_number || '',
      carrier: shipment.carrier || '',
      status: shipment.status || 'pending',
      actual_delivery: shipment.actual_delivery ? new Date(shipment.actual_delivery).toISOString().split('T')[0] : '',
      notes: shipment.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateShipment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingShipment) return;

    try {
      const { error: shippingError } = await supabase
        .from('shipping_info')
        .update({
          tracking_number: editFormData.tracking_number,
          carrier: editFormData.carrier,
          status: editFormData.status,
          actual_delivery: editFormData.actual_delivery || null,
          notes: editFormData.notes
        })
        .eq('id', editingShipment.id);

      if (shippingError) throw shippingError;

      // Update sales order status if shipment is delivered
      if (editFormData.status === 'delivered') {
        const { error: orderError } = await supabase
          .from('sales_orders')
          .update({
            status: 'delivered',
            delivery_date: editFormData.actual_delivery || new Date().toISOString().split('T')[0]
          })
          .eq('id', editingShipment.sales_order_id);

        if (orderError) throw orderError;
      }

      setShowEditModal(false);
      setEditingShipment(null);
      loadShipments();
    } catch (error) {
      console.error('Error updating shipment:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending: 'warning',
      processing: 'primary',
      in_transit: 'primary',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'danger',
      failed: 'danger'
    };
    return colors[status as keyof typeof colors] || 'secondary';
  };

  const stats = [
    {
      title: 'Pending Shipments',
      value: shipments.filter(s => (s.sales_orders?.status || s.status) === 'pending').length,
      icon: <Truck className="h-6 w-6 text-blue-600" />
    },
    {
      title: 'Processing/In Transit',
      value: shipments.filter(s => {
        const status = s.sales_orders?.status || s.status;
        return status === 'processing' || status === 'in_transit' || status === 'shipped';
      }).length,
      icon: <Package2 className="h-6 w-6 text-yellow-600" />
    },
    {
      title: 'Delivered',
      value: shipments.filter(s => (s.sales_orders?.status || s.status) === 'delivered').length,
      icon: <MapPin className="h-6 w-6 text-green-600" />
    },
    {
      title: 'Failed/Cancelled',
      value: shipments.filter(s => {
        const status = s.sales_orders?.status || s.status;
        return status === 'failed' || status === 'cancelled';
      }).length,
      icon: <Calendar className="h-6 w-6 text-red-600" />
    }
  ];

  const shippingMethods = [
    { value: 'ground', label: 'Ground Shipping' },
    { value: 'express', label: 'Express Shipping' },
    { value: 'overnight', label: 'Overnight Shipping' }
  ];


  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const orderOptions = pendingOrders.map(order => ({
    value: order.id,
    label: `#${order.order_number} - ${order.customer_name} ($${order.total_amount})`
  }));

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className="overflow-hidden rounded-lg">
        <div className="relative">
          <img
            src="https://images.pexels.com/photos/586103/pexels-photo-586103.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
            alt="Delivery trucks and shipping logistics operations center"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30 flex items-center">
            <div className="px-8 text-white">
              <h1 className="text-3xl font-bold mb-2">Shipping Management</h1>
              <p className="text-lg opacity-90">
                Coordinate shipments and delivery logistics efficiently
              </p>
              <div className="mt-4 flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Truck className="h-4 w-4 mr-2" />
                  <span>{shipments.length} Total Shipments</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span>{shipments.filter(s => (s.sales_orders?.status || s.status) === 'delivered').length} Delivered</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>{shipments.filter(s => (s.sales_orders?.status || s.status) === 'pending').length} Pending</span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute top-4 right-4">
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Shipment
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">{stat.value}</p>
                </div>
                {stat.icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <CardTitle>Create New Shipment</CardTitle>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <Select
                  label="Sales Order"
                  options={orderOptions}
                  value={formData.sales_order_id}
                  onChange={(value) => handleOrderSelect(value)}
                  error={orderOptions.length === 0 ? 'No pending orders available' : undefined}
                  fullWidth
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Shipping Method"
                    options={shippingMethods}
                    value={formData.shipping_method}
                    onChange={(value) => setFormData({ ...formData, shipping_method: value })}
                    fullWidth
                    required
                  />

                  <Input
                    label="Carrier"
                    name="carrier"
                    value={formData.carrier}
                    onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                    placeholder="e.g., FedEx, UPS, USPS, DHL"
                    fullWidth
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Tracking Number"
                    name="tracking_number"
                    value={formData.tracking_number}
                    onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                    fullWidth
                  />
                  
                  <Input
                    label="Shipping Cost"
                    type="number"
                    name="shipping_cost"
                    value={formData.shipping_cost}
                    onChange={(e) => setFormData({ ...formData, shipping_cost: e.target.value })}
                    fullWidth
                  />
                </div>

                <Input
                  label="Estimated Delivery Date"
                  type="date"
                  name="estimated_delivery"
                  value={formData.estimated_delivery}
                  onChange={(e) => setFormData({ ...formData, estimated_delivery: e.target.value })}
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipping Address
                  </label>
                  <textarea
                    name="shipping_address"
                    rows={3}
                    value={formData.shipping_address}
                    onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </CardContent>
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Shipment
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Recent Shipments</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
            </div>
          ) : shipments.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No shipments found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shipping Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tracking
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shipments.map((shipment) => (
                    <tr key={shipment.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          Order #{shipment.sales_orders?.order_number || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {shipment.sales_orders?.customer_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {shipment.shipping_method}
                        </div>
                        <div className="text-sm text-gray-500">
                          via {shipment.carrier}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getStatusColor(shipment.sales_orders?.status || shipment.status)}>
                          {(shipment.sales_orders?.status || shipment.status).replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {shipment.tracking_number ? (
                          <div className="text-sm text-blue-600 hover:text-blue-800">
                            {shipment.tracking_number}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Not available</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => handleEditClick(shipment)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-900"
                            onClick={() => navigate(`/sales-orders/${shipment.sales_order_id}`)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {showEditModal && editingShipment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle>Update Tracking Information</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Order #{editingShipment.sales_orders?.order_number || 'N/A'}
              </p>
            </CardHeader>
            <form onSubmit={handleUpdateShipment}>
              <CardContent className="space-y-4">
                <Input
                  label="Carrier"
                  value={editFormData.carrier}
                  onChange={(e) => setEditFormData({ ...editFormData, carrier: e.target.value })}
                  placeholder="e.g., FedEx, UPS, USPS, DHL"
                  fullWidth
                />

                <Input
                  label="Tracking Number"
                  value={editFormData.tracking_number}
                  onChange={(e) => setEditFormData({ ...editFormData, tracking_number: e.target.value })}
                  placeholder="Enter tracking number"
                  fullWidth
                />

                <Select
                  label="Status"
                  options={statusOptions}
                  value={editFormData.status}
                  onChange={(value) => setEditFormData({ ...editFormData, status: value })}
                  fullWidth
                  required
                />

                <Input
                  label="Actual Delivery Date"
                  type="date"
                  value={editFormData.actual_delivery}
                  onChange={(e) => setEditFormData({ ...editFormData, actual_delivery: e.target.value })}
                  fullWidth
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Add any notes about the shipment..."
                  />
                </div>
              </CardContent>
              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingShipment(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update Tracking
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ShippingPage;